import React, { type MouseEvent, type ReactNode, useCallback, useRef } from 'react';

import { gsap } from 'gsap';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { clearManagerSelection, setManagerStartDragging } from '@app/store/actions/nft-manager';

import { ChildrenWrapper, DraggableWrapper, StyledDraggableIcon } from './styles';

const HOVERING_ROTATION = 3;

export interface DraggableProps {
    id: string;
    allowDragging?: boolean;
    dragged?: boolean;
    children?: ReactNode;
    // TODO: Passing `className` to components
    // is an anti-pattern and should be avoided.
    className?: string;
    onHover(state: boolean): void;
}

export function Draggable({ id, dragged, allowDragging = false, children, className, onHover }: DraggableProps) {
    const { tablet } = useMediaQuery();

    const childRef = useRef<HTMLDivElement>(null);
    const hoveringRef = useRef(false);

    const dispatch = useAppDispatch();
    const draggingItems = useAppSelector((state) => state.nftManager.draggingItems);
    const selectedItems = useAppSelector((state) => state.nftManager.selectedItems);

    const dragging = draggingItems.some((item) => item === id);
    const selected = selectedItems.some((itemId) => itemId === id);

    const getDraggingProperties = useCallback(() => {
        if (!childRef.current) return;

        return {
            id,
            element: childRef.current,
            existingRotation: hoveringRef.current ? HOVERING_ROTATION : undefined,
        };
    }, [id]);

    const handleDragStart = (event: MouseEvent) => {
        const properties = getDraggingProperties();
        if (!properties) return;

        event.preventDefault();

        if (!selected) dispatch(clearManagerSelection());

        dispatch(setManagerStartDragging(event.nativeEvent, id));
    };

    const handleMouseOver = () => {
        hoveringRef.current = true;
        onHover(true);

        if (!childRef.current) return;

        gsap.set(childRef.current, { rotate: HOVERING_ROTATION });
    };

    const handleMouseLeave = () => {
        hoveringRef.current = false;
        onHover(false);

        if (!childRef.current) return;

        gsap.set(childRef.current, { rotate: 0 });
    };

    return (
        <DraggableWrapper className={className} $draggable={tablet} $dragging={dragging && !dragged}>
            {tablet && allowDragging && (
                <StyledDraggableIcon
                    width={16}
                    onMouseDown={handleDragStart}
                    onMouseOver={handleMouseOver}
                    onMouseLeave={handleMouseLeave}
                />
            )}

            <ChildrenWrapper id={`draggable-wrapper-${id}`} ref={childRef}>
                {children}
            </ChildrenWrapper>
        </DraggableWrapper>
    );
}
